import {
    AddOutlined,
    ArrowBack,
    CheckOutlined,
    CloseOutlined,
    ContentCopyOutlined,
    DeleteOutlined,
    DescriptionOutlined,
} from '@mui/icons-material';
import { Box, Button, Stack, ThemeProvider, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import Loader from 'components/v2/common/loader';
import PageHeader from 'components/v2/common/pageHeader';
import Result from 'components/v2/common/Result';
import PageBackground from 'components/v2/PageBackground';
import { TemplatePreviewDialog } from 'views/v2/retainerAgreement/components/templatePreviewDialog';
import { TemplatesTable } from 'views/v2/retainerAgreement/components/templatesTable';
import {
    TemplatesTypeFilter,
    type TemplateTypeFilterValue,
} from 'views/v2/retainerAgreement/components/templatesTypeFilter';

import { FEATURE } from 'constant/features';
import { useHasFeature } from 'hooks/useHasFeature';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import {
    bulkDeleteTemplates,
    bulkDuplicateTemplates,
    bulkPublishTemplates,
    bulkUnpublishTemplates,
    deleteTemplate,
    duplicateTemplate,
    loadTemplates,
    toggleFavorite,
} from 'redux/retainer-templates/templates.middleware';
import { setFilters } from 'redux/retainer-templates/templates.slice';
import type { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import type { Template } from 'types';
import { FEATURE_NOT_ENABLED_MSG, isFeatureNotEnabledError } from 'utils/features';
import { getErrorMessage } from 'utils/v2/errorHelper';

export default function TemplatesPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const canManageTemplates = useHasFeature(FEATURE.RETAINER_TEMPLATES);

    const {
        list: templates,
        filters,
        error,
        isLoading,
    } = useSelector((state: RootState) => state.templates);
    const [listReady, setListReady] = useState(false);

    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
    const [fullPreviewTemplate, setFullPreviewTemplate] = useState<Template | null>(null);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    useEffect(() => {
        let active = true;
        void dispatch(loadTemplates(filters)).finally(() => {
            if (active) setListReady(true);
        });
        return () => {
            active = false;
        };
    }, [dispatch, filters]);

    useEffect(() => {
        if (
            error &&
            error !== FEATURE_NOT_ENABLED_MSG &&
            !isFeatureNotEnabledError({ msg: error })
        ) {
            dispatch(setAlert({ message: error, type: 'error' }));
        }
    }, [dispatch, error]);

    const handleCreateTemplate = () => {
        navigate('/templates/new');
    };

    const handleEdit = (template: Template) => {
        navigate(`/templates/${template.id}/edit`);
    };

    const handlePreview = (template: Template) => {
        setFullPreviewTemplate(template);
    };

    const notifySuccess = (message: string) => {
        dispatch(setAlert({ message, type: 'success' }));
    };

    const notifyError = (err: unknown, fallback: string) => {
        if (isFeatureNotEnabledError(err)) return;
        dispatch(setAlert({ message: getErrorMessage(err, fallback), type: 'error' }));
    };

    const handleDuplicate = async (template: Template) => {
        try {
            await dispatch(duplicateTemplate(Number(template.id))).unwrap();
            notifySuccess(translate('templateDuplicated'));
        } catch (err) {
            notifyError(err, translate('templateDuplicateFailed'));
        }
    };

    const handleDelete = (template: Template) => {
        setTemplateToDelete(template);
    };

    const confirmDelete = async () => {
        if (!templateToDelete) return;

        try {
            setDeleteLoading(true);
            setDeleteError(null);
            await dispatch(deleteTemplate(Number(templateToDelete.id))).unwrap();
            setSelectedIds((ids) => ids.filter((id) => id !== templateToDelete.id));
            notifySuccess(translate('templateDeleted'));
            setTemplateToDelete(null);
        } catch (err) {
            if (isFeatureNotEnabledError(err)) {
                setTemplateToDelete(null);
                return;
            }
            setDeleteError(getErrorMessage(err, translate('templateDeleteFailed')));
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        try {
            await dispatch(
                bulkDeleteTemplates({ template_ids: selectedIds.map((id) => Number(id)) })
            ).unwrap();
            setSelectedIds([]);
            notifySuccess(translate('templatesDeleted'));
        } catch (err) {
            notifyError(err, translate('templatesDeleteFailed'));
        }
    };

    const handleBulkPublish = async () => {
        if (selectedIds.length === 0) return;
        try {
            await dispatch(
                bulkPublishTemplates({ template_ids: selectedIds.map((id) => Number(id)) })
            ).unwrap();
            notifySuccess(translate('templatesPublished'));
        } catch (err) {
            notifyError(err, translate('templatesPublishFailed'));
        }
    };

    const handleBulkUnpublish = async () => {
        if (selectedIds.length === 0) return;
        try {
            await dispatch(
                bulkUnpublishTemplates({ template_ids: selectedIds.map((id) => Number(id)) })
            ).unwrap();
            notifySuccess(translate('templatesUnpublished'));
        } catch (err) {
            notifyError(err, translate('templatesUnpublishFailed'));
        }
    };

    const handleBulkDuplicate = async () => {
        if (selectedIds.length === 0) return;
        try {
            await dispatch(
                bulkDuplicateTemplates({ template_ids: selectedIds.map((id) => Number(id)) })
            ).unwrap();
            setSelectedIds([]);
            notifySuccess(translate('templatesDuplicated'));
        } catch (err) {
            notifyError(err, translate('templatesDuplicateFailed'));
        }
    };

    const handleToggleFavorite = async (template: Template) => {
        try {
            await dispatch(toggleFavorite(Number(template.id))).unwrap();
        } catch (err) {
            notifyError(err, translate('templateFavoriteFailed'));
        }
    };

    const handleTypeFilterChange = (type: TemplateTypeFilterValue) => {
        dispatch(setFilters({ type }));
    };

    const handleClearFilters = () => {
        dispatch(
            setFilters({
                search: '',
                type: 'all',
                country: '',
                program: '',
                status: 'all',
                hasLogo: 'all',
                pricingModel: 'all',
            })
        );
    };

    const isEmptyLibrary = templates.length === 0 && filters.type === 'all';
    const showListLoader = templates.length === 0 && (isLoading || !listReady);

    return (
        <ThemeProvider theme={appThemeV2}>
            <PageBackground
                maxWidth="xl"
                rootSx={{ overflow: 'auto', pt: 7 }}
                sx={{ ml: 'inherit' }}
            >
                <PageHeader title="manageTemplates" description="manageTemplatesDescription">
                    <Stack direction="row" sx={{ gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBack />}
                            onClick={() => navigate(-1)}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            {translate('back')}
                        </Button>
                        {canManageTemplates ? (
                            <Button
                                variant="contained"
                                startIcon={<AddOutlined />}
                                onClick={handleCreateTemplate}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                {translate('createTemplate')}
                            </Button>
                        ) : null}
                    </Stack>
                </PageHeader>

                {canManageTemplates && selectedIds.length > 0 ? (
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        useFlexGap
                        sx={{
                            mb: 2.5,
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, px: 1 }}>
                            {translate('templatesSelected').replace(
                                '{{count}}',
                                String(selectedIds.length)
                            )}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Button
                            size="small"
                            startIcon={<CheckOutlined />}
                            onClick={() => void handleBulkPublish()}
                            sx={{ textTransform: 'none', color: 'success.main' }}
                        >
                            {translate('publish')}
                        </Button>
                        <Button
                            size="small"
                            startIcon={<CloseOutlined />}
                            onClick={() => void handleBulkUnpublish()}
                            sx={{ textTransform: 'none', color: 'warning.main' }}
                        >
                            {translate('unpublish')}
                        </Button>
                        <Button
                            size="small"
                            startIcon={<ContentCopyOutlined />}
                            onClick={() => void handleBulkDuplicate()}
                            sx={{ textTransform: 'none' }}
                        >
                            {translate('duplicate')}
                        </Button>
                        <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteOutlined />}
                            onClick={() => void handleBulkDelete()}
                            sx={{ textTransform: 'none' }}
                        >
                            {translate('delete')}
                        </Button>
                    </Stack>
                ) : null}

                <TemplatesTypeFilter
                    value={(filters.type as TemplateTypeFilterValue) || 'all'}
                    onChange={handleTypeFilterChange}
                />

                {showListLoader ? (
                    <Loader message={translate('loading')} isCircular rootSx={{ py: 8 }} />
                ) : isEmptyLibrary ? (
                    <Result
                        icon={<DescriptionOutlined sx={{ fontSize: 32 }} />}
                        title={translate('noTemplatesYet')}
                        description={translate(
                            canManageTemplates
                                ? 'noTemplatesYetDescription'
                                : 'noTemplatesYetReadonlyDescription'
                        )}
                    />
                ) : templates.length === 0 ? (
                    <Stack spacing={2}>
                        <Result
                            title={translate('noMatchingTemplates')}
                            description={translate('noMatchingTemplatesDescription')}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                sx={{ textTransform: 'none' }}
                            >
                                {translate('clearFilters')}
                            </Button>
                        </Box>
                    </Stack>
                ) : (
                    <TemplatesTable
                        templates={templates}
                        selectedIds={selectedIds}
                        onSelectionChange={setSelectedIds}
                        onPreview={handlePreview}
                        onEdit={handleEdit}
                        onDuplicate={handleDuplicate}
                        onDelete={handleDelete}
                        onToggleFavorite={handleToggleFavorite}
                        error={deleteError}
                        onClearError={() => setDeleteError(null)}
                    />
                )}

                {templateToDelete ? (
                    <ConfirmationDialog
                        title={translate('deleteTemplate')}
                        content={translate('deleteTemplateConfirm').replace(
                            '{{name}}',
                            templateToDelete.name
                        )}
                        loading={deleteLoading}
                        onClose={() => setTemplateToDelete(null)}
                        onConfirm={() => void confirmDelete()}
                    />
                ) : null}

                <TemplatePreviewDialog
                    template={fullPreviewTemplate}
                    open={!!fullPreviewTemplate}
                    onClose={() => setFullPreviewTemplate(null)}
                    onEditBranding={
                        canManageTemplates
                            ? () => {
                                  if (fullPreviewTemplate) {
                                      navigate(`/templates/${fullPreviewTemplate.id}/edit`);
                                  }
                              }
                            : undefined
                    }
                />
            </PageBackground>
        </ThemeProvider>
    );
}
